import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  AdminPanelSettings as AdminIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  InsightsOutlined as AnalyzeIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { sendMessage, analyzeLastWorkout, submitForm, type SSEEvent, type FormSubmitResult } from '../services/adminAgentService';

const CHART_COLORS = ['#7dd3fc', '#8bffb0', '#fbbf24', '#f87171', '#a78bfa', '#fb923c'];

interface ChatEntry {
  role: 'user' | 'assistant' | 'render' | 'tool' | 'error';
  content?: string;
  render?: RenderInstruction;
  toolName?: string;
}

interface RenderInstruction {
  component: 'table' | 'form' | 'chart';
  props: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Sub-components for render instructions
// ---------------------------------------------------------------------------

function RenderTable({ props }: { props: any }) {
  const { title, columns, rows } = props;
  return (
    <Box sx={{ my: 1 }}>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col: string, i: number) => (
                <TableCell key={i} sx={{ fontWeight: 700 }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any[], i: number) => (
              <TableRow key={i}>
                {row.map((cell: any, j: number) => (
                  <TableCell key={j}>
                    {cell === null || cell === undefined ? '—' : String(cell)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function RenderChart({ props }: { props: any }) {
  const { title, chartType, data, xKey, yKeys, yLabels } = props;

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey={xKey} stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <RechartsTooltip />
              <Legend />
              {yKeys.map((key: string, i: number) => (
                <Bar key={key} dataKey={key} name={yLabels[i]} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey={xKey} stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <RechartsTooltip />
              <Legend />
              {yKeys.map((key: string, i: number) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={yLabels[i]}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey={yKeys[0]}
                nameKey={xKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }: any) => `${name}: ${value}`}
              >
                {data.map((_: any, i: number) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return <Typography color="error">Unknown chart type: {chartType}</Typography>;
    }
  };

  return (
    <Box sx={{ my: 1 }}>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      )}
      {renderChart()}
    </Box>
  );
}

function RenderForm({ props, onSubmitResult }: { props: any; onSubmitResult: (result: FormSubmitResult) => void }) {
  const { title, fields, form_id, submit_label } = props;
  const [values, setValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    for (const field of fields) {
      initial[field.name] = field.defaultValue ?? (field.type === 'checkbox' || field.type === 'switch' ? false : '');
    }
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await submitForm(form_id, values);
      onSubmitResult(result);
    } catch (e: any) {
      onSubmitResult({ success: false, error: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ my: 1, p: 2 }}>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 2 }}>{title}</Typography>
      )}
      <Stack spacing={2}>
        {fields.map((field: any) => {
          switch (field.type) {
            case 'select':
              return (
                <Select
                  key={field.name}
                  value={values[field.name] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                  size="small"
                  displayEmpty
                  renderValue={(val) => val || field.label}
                >
                  {(field.options || []).map((opt: string) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              );
            case 'checkbox':
              return (
                <FormControlLabel
                  key={field.name}
                  control={
                    <Checkbox
                      checked={!!values[field.name]}
                      onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.checked }))}
                    />
                  }
                  label={field.label}
                />
              );
            case 'switch':
              return (
                <FormControlLabel
                  key={field.name}
                  control={
                    <Switch
                      checked={!!values[field.name]}
                      onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.checked }))}
                    />
                  }
                  label={field.label}
                />
              );
            default:
              return (
                <TextField
                  key={field.name}
                  label={field.label}
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={values[field.name] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                  size="small"
                  required={field.required}
                />
              );
          }
        })}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {submit_label || 'Submit'}
        </Button>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AdminAgent: React.FC = () => {
  const [input, setInput] = useState('');
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `admin-${Date.now()}`);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries]);

  // Shared streaming runner: shows `userContent` as the user's message, then
  // pipes the agent's SSE events into the transcript. `stream` starts the
  // backend call (chat, analyze-last-workout, ...) with the given event handler.
  const runAgentStream = useCallback(
    async (userContent: string, stream: (onEvent: (event: SSEEvent) => void) => Promise<void>) => {
      setEntries((prev) => [...prev, { role: 'user', content: userContent }]);
      setLoading(true);

      let textBuffer = '';

      try {
        await stream((event: SSEEvent) => {
          switch (event.type) {
            case 'text':
              textBuffer += event.content || '';
              setEntries((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return [...prev.slice(0, -1), { role: 'assistant', content: textBuffer }];
                }
                return [...prev, { role: 'assistant', content: textBuffer }];
              });
              break;

            case 'tool_call':
              textBuffer = '';
              setEntries((prev) => [...prev, { role: 'tool', toolName: event.name }]);
              break;

            case 'render':
              textBuffer = '';
              setEntries((prev) => [
                ...prev,
                {
                  role: 'render',
                  render: {
                    component: event.component!,
                    props: event.props!,
                  },
                },
              ]);
              break;

            case 'error':
              setEntries((prev) => [...prev, { role: 'error', content: event.content }]);
              break;

            case 'done':
              break;
          }
        });
      } catch (e: any) {
        setEntries((prev) => [...prev, { role: 'error', content: e.message }]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleSend = useCallback(async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    await runAgentStream(msg, (onEvent) => sendMessage(msg, sessionId, onEvent));
  }, [input, loading, sessionId, runAgentStream]);

  const handleAnalyzeLastWorkout = useCallback(async () => {
    if (loading) return;
    await runAgentStream(
      'Анализирай последната ми тренировка',
      (onEvent) => analyzeLastWorkout(sessionId, onEvent),
    );
  }, [loading, sessionId, runAgentStream]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFormResult = (result: FormSubmitResult) => {
    if (result.success) {
      setEntries((prev) => [
        ...prev,
        { role: 'assistant', content: `Form submitted successfully: ${JSON.stringify(result.result, null, 2)}` },
      ]);
    } else {
      setEntries((prev) => [
        ...prev,
        { role: 'error', content: `Form error: ${result.error}` },
      ]);
    }
  };

  const handleClear = () => {
    setEntries([]);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AdminIcon color="primary" />
        <Typography variant="h5">Admin Agent</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          startIcon={<AnalyzeIcon />}
          onClick={handleAnalyzeLastWorkout}
          disabled={loading}
        >
          Анализирай последната тренировка
        </Button>
        <Button
          size="small"
          color="inherit"
          startIcon={<DeleteIcon />}
          onClick={handleClear}
          disabled={entries.length === 0}
        >
          Clear
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2, minHeight: 400, maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
        <CardContent sx={{ p: 2 }} data-testid="admin-agent-transcript">
          {entries.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <AdminIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1">
                Ask me to query data, manage exercises, programs, or workouts.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                I can execute SDK operations and render tables, charts, and forms.
              </Typography>
            </Box>
          )}

          {entries.map((entry, i) => {
            switch (entry.role) {
              case 'user':
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                    <Paper
                      sx={{
                        px: 2,
                        py: 1,
                        maxWidth: '80%',
                        bgcolor: 'primary.dark',
                        borderRadius: 3,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.content}
                      </Typography>
                    </Paper>
                  </Box>
                );

              case 'assistant':
                return (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Paper sx={{ px: 2, py: 1, maxWidth: '90%', borderRadius: 3 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.content}
                      </Typography>
                    </Paper>
                  </Box>
                );

              case 'tool':
                return (
                  <Box key={i} sx={{ mb: 1, display: 'flex', gap: 0.5 }}>
                    <Chip
                      icon={<CodeIcon />}
                      label={entry.toolName}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  </Box>
                );

              case 'render':
                if (!entry.render) return null;
                switch (entry.render.component) {
                  case 'table':
                    return <RenderTable key={i} props={entry.render.props} />;
                  case 'chart':
                    return <RenderChart key={i} props={entry.render.props} />;
                  case 'form':
                    return <RenderForm key={i} props={entry.render.props} onSubmitResult={handleFormResult} />;
                  default:
                    return null;
                }

              case 'error':
                return (
                  <Alert key={i} severity="error" sx={{ mb: 1 }}>
                    {entry.content}
                  </Alert>
                );

              default:
                return null;
            }
          })}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Thinking...</Typography>
            </Box>
          )}

          <div ref={chatEndRef} />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'admin-agent-input' }}
          placeholder="Ask something... (e.g. 'List all exercises' or 'Show my recent workouts')"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          multiline
          maxRows={4}
        />
        <IconButton
          color="primary"
          aria-label="Send admin message"
          data-testid="admin-agent-send-button"
          onClick={handleSend}
          disabled={!input.trim() || loading}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default AdminAgent;
